import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Mail, MapPin, Clock, Shield } from 'lucide-react';

const ContactPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Eye className="w-10 h-10 text-purple-400" />
            <span className="text-2xl font-bold gradient-text serif-display">Coercive Control Patterns</span>
          </div>
          
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="glass rounded-2xl p-8">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Mail className="w-10 h-10 text-purple-400" />
              <h1 className="text-4xl font-bold text-white serif-display">Contact Us</h1>
            </div>
            <p className="text-xl text-gray-300 leading-relaxed">
              We're here to help with questions about our service, technical support, or general inquiries.
            </p>
          </div>

          {/* Main Contact Information */}
          <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-lg p-8 mb-8 border border-purple-500/30">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-3 mb-6">
                <Mail className="w-8 h-8 text-purple-400" />
                <h2 className="text-3xl font-bold text-white">Email Support</h2>
              </div>
              
              <div className="mb-6">
                <a 
                  href="mailto:support@coercivecontrolpatterns.com"
                  className="text-2xl text-purple-300 hover:text-purple-100 transition-colors underline font-medium"
                >
                  support@coercivecontrolpatterns.com
                </a>
              </div>
              
              <p className="text-lg text-gray-300">
                Our primary communication method for all inquiries, technical support, and assistance.
              </p>
            </div>
          </div>

          {/* Support Information */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            
            <div className="bg-slate-800/50 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Clock className="w-8 h-8 text-blue-400" />
                <h3 className="text-2xl font-bold text-white">Response Times</h3>
              </div>
              <div className="space-y-3 text-gray-300">
                <div>
                  <p className="font-semibold text-blue-300">General Inquiries</p>
                  <p>We aim to respond within 24-48 hours</p>
                </div>
                <div>
                  <p className="font-semibold text-blue-300">Technical Issues</p>
                  <p>Priority response within 12-24 hours</p>
                </div>
                <div>
                  <p className="font-semibold text-blue-300">Account Issues</p>
                  <p>Matters addressed within 12-24 hours</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="w-8 h-8 text-green-400" />
                <h3 className="text-2xl font-bold text-white">Privacy Notice</h3>
              </div>
              <div className="space-y-3 text-gray-300">
                <p>
                  All communications are handled confidentially and securely.
                </p>
                <p>
                  We never share your contact information or inquiry details with third parties.
                </p>
                <p>
                  For sensitive matters, our support team follows strict privacy protocols.
                </p>
              </div>
            </div>

          </div>

          {/* What to Include */}
          <div className="bg-slate-800/50 rounded-lg p-6 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">When Contacting Us, Please Include:</h3>
            <div className="grid md:grid-cols-2 gap-6">
              
              <div>
                <h4 className="text-lg font-semibold text-purple-300 mb-3">For Technical Support</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                  <li>Description of the issue you're experiencing</li>
                  <li>Steps you took before the issue occurred</li>
                  <li>Browser type and version (if applicable)</li>
                  <li>Any error messages you received</li>
                  <li>Your account email (if relevant)</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-green-300 mb-3">For General Inquiries</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                  <li>Clear subject line describing your question</li>
                  <li>Relevant details about your situation</li>
                  <li>Preferred response method (email reply)</li>
                  <li>Any deadlines or urgency level</li>
                  <li>Reference to specific policies if applicable</li>
                </ul>
              </div>

            </div>
          </div>

          {/* Business Information */}
          <div className="bg-slate-800/50 rounded-lg p-6 mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <MapPin className="w-6 h-6 text-amber-400" />
              <h3 className="text-2xl font-bold text-white">Business Information</h3>
            </div>
            <div className="space-y-2 text-gray-300">
              <p><strong>Legal Jurisdiction:</strong> State of Wyoming, United States</p>
              <p><strong>Data Processing Location:</strong> United States</p>
              <p><strong>Service Availability:</strong> United States and Canada</p>
              <p><strong>Business Type:</strong> AI-Powered Communication Analysis Service</p>
            </div>
          </div>

          {/* Common Questions */}
          <div className="bg-slate-800/50 rounded-lg p-6">
            <h3 className="text-2xl font-bold text-white mb-6">Common Contact Reasons</h3>
            <div className="grid md:grid-cols-2 gap-6">
              
              <div>
                <h4 className="text-lg font-semibold text-blue-300 mb-3">Account & Billing</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
                  <li>Payment processing issues</li>
                  <li>Refund requests for technical failures</li>
                  <li>Account access problems</li>
                  <li>2FA verification issues</li>
                  <li>Receipt or billing questions</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-amber-300 mb-3">Service & Reports</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-300 text-sm">
                  <li>Analysis processing questions</li>
                  <li>Privacy and data deletion confirmation</li>
                  <li>Service limitations clarification</li>
                </ul>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ContactPage;